import { MdOutlinedTextField } from "@material/web/textfield/outlined-text-field"
import { CheckRes, LayoutState, PartData, UploadRes } from "../types/types"
import { getErrors } from "../utils/checkLayoutErrors"
import { addEmptyLayout, createCloseIcon, createCloudIcon, createInfoIcon, createModal, createTimeIcon } from "../utils/createElemets"
import { uploadWithProgress } from "../utils/uploadWithProgress"
import { createLayoutErrorsModal } from "../utils/createLayoutErrorsModal"
import { BASE_API_URL, LayoutsUploadModal } from "../layouts-upload-modal"
import { filterAllowedFiles } from "../utils/allowedFilesFilter"

// --------------------------------------------------------------------------------

export class UploadZoneMultipleTypes {
    #uploadZone: HTMLDivElement

    #mainContainer: HTMLDivElement
    #circulationContainer: HTMLDivElement

    #dropZoneContainer: HTMLDivElement
    #label: HTMLLabelElement
    #span: HTMLSpanElement

    #layoutsContainer: HTMLDivElement
    #filledLayoutsContainer: HTMLDivElement
    #emptyLayoutsContainer: HTMLDivElement

    #filledLayoutsSerial: number
    #emptyLayoutsSerial: number

    #partData?: PartData
    #uploadState: 'ready' | 'uploading' | 'check' | 'result'
    #checkProgress: number

    #readyElements: HTMLElement[]
    #modal: LayoutsUploadModal
    #circulationQty: number
    #circulationRemainder: HTMLElement
    #circulationQtyContainer: HTMLDivElement
    #circulationInfo: HTMLSpanElement
    #circulationActions: HTMLDivElement

    #remainderValue: Record<number, number>
    #qtyInputs: MdOutlinedTextField[]
    #nameInputs: MdOutlinedTextField[]

    #distributeInput: HTMLInputElement
    #fillNamesInput: HTMLInputElement

    constructor(partData: PartData, modal: LayoutsUploadModal) {
        this.#partData = partData
        this.#modal = modal
        this.#remainderValue = {}

        this.#qtyInputs = []
        this.#nameInputs = []

        const circulationQtyContainer = document.querySelector('.product-calc-circulation') as HTMLInputElement | null
        this.#circulationQty = circulationQtyContainer
            ? +circulationQtyContainer.value.replaceAll(' ', '')
            : window.circulation
                ? +window.circulation
                : 1

        this.#filledLayoutsSerial = 0
        this.#emptyLayoutsSerial = 0 + layoutsState[partData.id].filter(state => !!state).length
        this.#checkProgress = 0

        this.#uploadZone = document.createElement('div')
        this.#uploadZone.classList.add('upload-zone', 'multiple-types')

        this.#mainContainer = document.createElement('div')
        this.#mainContainer.classList.add('main-container')
        this.#circulationContainer = document.createElement('div')
        this.#circulationContainer.classList.add('circulation-container')

        this.#dropZoneContainer = document.createElement('div')
        this.#dropZoneContainer.classList.add('drop-zone')

        this.#label = document.createElement('label')
        this.#label.classList.add('ready')
        this.#span = document.createElement('span')

        this.#uploadState = 'ready'

        // Ready state elements ----------
        const readyDesktop = document.createElement('div')
        readyDesktop.classList.add('desktop')
        readyDesktop.textContent = 'Перетащите файл или'
        readyDesktop.prepend(createCloudIcon())

        const readyMobile = document.createElement('div')
        readyMobile.classList.add('mobile')
        readyMobile.textContent = 'Загрузить файл'

        const allowedFilesString = window.layoutExtensions
            ? '.' + window.layoutExtensions?.reduce((acc, value) => acc + ', .' + value) : ''

        const input = document.createElement('input')
        input.type = 'file'
        if (window.layoutExtensions)
            input.accept = allowedFilesString
        input.multiple = true
        input.onchange = async (e: Event) => {
            const inputTarget = e.target as HTMLInputElement
            if (!inputTarget.files) return

            const filteredFiles = filterAllowedFiles(Array.from(inputTarget.files))
            if (filteredFiles.length === 0) {
                createModal({
                    title: 'Файл не поддерживается',
                    subTitle: 'Только: ' + allowedFilesString,
                    submitText: 'Ok'
                }, this.#modal.shadowRoot)
                return
            }

            const maxFilesQty = layoutsState[partData.id].filter(state => !state).length

            for (const [index, file] of Array.from(inputTarget.files).entries())
                if (index < maxFilesQty) {
                    const isSuccsess = await this.#uploadFile(file)
                    if (!isSuccsess) break
                }


            inputTarget.files = null
            inputTarget.value = ''
        }

        const readyDndLayer = document.createElement('div')
        readyDndLayer.classList.add('dnd-layer')
        readyDndLayer.ondragenter = e => this.#label.classList.add('hovered')
        readyDndLayer.ondragleave = e => this.#label.classList.remove('hovered')
        readyDndLayer.ondrop = async (e: DragEvent) => {
            e.preventDefault()
            if (!e.dataTransfer) return

            const dataList: File[] = []
            const maxFilesQty = layoutsState[partData.id].filter(state => !state).length

            if (e.dataTransfer.items)
                Array.from(e.dataTransfer.items).forEach((item, i) => {
                    if (item.kind === "file") {
                        const file = item.getAsFile()
                        file && dataList.push(file)
                    }
                })
            else
                Array.from(e.dataTransfer.files).forEach((file, i) => {
                    dataList.push(file)
                })

            const filteredFiles = filterAllowedFiles(dataList)
            if (filteredFiles.length === 0) {
                createModal({
                    title: 'Файл не поддерживается',
                    subTitle: 'Только: ' + allowedFilesString,
                    submitText: 'Ok'
                }, this.#modal.shadowRoot)
                return
            }

            for (const [index, file] of dataList.entries())
                if (index < maxFilesQty) {
                    const isSuccsess = await this.#uploadFile(file)
                    if (!isSuccsess) break
                }

            e.dataTransfer.clearData()
        }

        this.#readyElements = [readyDesktop, readyMobile, input, readyDndLayer]

        this.#span.textContent = 'Выберите макет с компьютера'
        this.#label.append(...this.#readyElements, this.#span)
        this.#dropZoneContainer.append(this.#label)

        // Circulation ----------

        this.#circulationInfo = document.createElement('span')
        this.#circulationInfo.classList.add('circulation-info')
        this.#circulationInfo.innerHTML = 'Остаток должен быть — 0,<br /> чтобы продолжить оформление заказа'

        this.#circulationQtyContainer = document.createElement('div')
        this.#circulationQtyContainer.classList.add('circulation-qty')

        const circulationTarget = document.createElement('div')
        const target = document.createElement('b')
        target.textContent = `${this.#circulationQty}`
        circulationTarget.append('Тираж', target)

        const circulationLeft = document.createElement('div')
        this.#circulationRemainder = document.createElement('b')
        this.#circulationRemainder.textContent = `${this.#circulationQty}`
        circulationLeft.append('Остаток', this.#circulationRemainder)

        this.#circulationQtyContainer.append(circulationTarget, circulationLeft)

        this.#circulationActions = document.createElement('div')
        this.#circulationActions.classList.add('circulation-actions')

        const distributeLabel = document.createElement('label')
        this.#distributeInput = document.createElement('input')
        this.#distributeInput.type = 'checkbox'
        this.#distributeInput.onchange = e => {
            const target = e.currentTarget as HTMLInputElement
            if (target.checked === true) {
                this.#autofillQty()

            } else {
                this.#remainder = {}
                layoutsState[partData.id].forEach((state, index) => {
                    if (!state) return

                    state.qty = undefined

                    this.#qtyInputs[index].value = ``
                    this.#qtyInputs[index].disabled = false
                })
            }
        }
        distributeLabel.append(this.#distributeInput, 'Распределить тираж равномерно')

        const fillNamesLabel = document.createElement('label')
        this.#fillNamesInput = document.createElement('input')
        this.#fillNamesInput.type = 'checkbox'
        this.#fillNamesInput.onchange = e => {
            const target = e.currentTarget as HTMLInputElement
            if (target.checked === true) {
                this.#autofillNames()

            } else {
                layoutsState[partData.id].forEach((layoutState) => {
                    if (layoutState)
                        layoutState.name = ''
                })

                this.#updateLayouts()
            }

        }
        fillNamesLabel.append(this.#fillNamesInput, 'Использовать имя файла для наименования вида')
        this.#circulationActions.append(distributeLabel, fillNamesLabel)

        this.#circulationContainer.append(this.#circulationQtyContainer)

        // Upload zone elements ----------

        const span = document.createElement('span')
        span.classList.add('subtitle')
        span.textContent = 'После загрузки и проверки файлы появятся ниже'

        this.#layoutsContainer = document.createElement('div')
        this.#layoutsContainer.classList.add('layouts-container')

        this.#filledLayoutsContainer = document.createElement('div')
        this.#emptyLayoutsContainer = document.createElement('div')
        this.#layoutsContainer.append(this.#filledLayoutsContainer, this.#emptyLayoutsContainer)

        if (!partData.numberSpecies) return

        this.#mainContainer.append(span, this.#dropZoneContainer, this.#layoutsContainer)
        this.#uploadZone.append(this.#mainContainer, this.#circulationContainer)

        layoutsState[partData.id].forEach((layoutState, index) => {
            this.#layoutState = { layoutState, index }
        })
    }

    #autofillQty() {
        if (!this.#partData) return

        const types = layoutsState[this.#partData.id].filter(state => !!state).length
        const rounded = Math.floor(this.#circulationQty / types)
        let remainder = this.#circulationQty % types

        layoutsState[this.#partData.id]
            .forEach((state, index) => {
                if (!state) return

                const value = rounded + (remainder > 0 ? 1 : 0)

                state.qty = value
                this.#remainder = { ...this.#remainder, [index]: value }

                this.#qtyInputs[index].value = `${value}`
                this.#qtyInputs[index].disabled = true
                remainder--
            })
    }

    #autofillNames() {
        if (!this.#partData) return

        layoutsState[this.#partData.id].forEach((state, index) => {
            if (!state) return

            state.name = state.uploadResData.original_name ?? ''
            this.#nameInputs[index].value = state.uploadResData.original_name ?? ''
            this.#nameInputs[index].disabled = true
        })
    }

    #updateRemainder() {
        const qtyList = Object.values(this.#remainder)
        const sum = qtyList.length ? qtyList.reduce((acc, value) => acc + value) : 0
        const remainder = this.#circulationQty - sum

        this.#circulationRemainder.textContent = `${remainder}`
        this.#circulationRemainder.parentElement?.classList.toggle('negative', remainder < 0)
        this.#circulationInfo.classList.toggle('ok', remainder === 0)

        const hasWarnings = Object.values(layoutsState).flat().map(layout => layout?.warnings).includes(true)

        if (remainder === 0) {
            this.#circulationInfo.innerHTML = 'Тираж распределен, можете продолжить оформление заказа'
            if (!hasWarnings || this.#modal.agreementIsChecked)
                this.#modal.enableSubmitBtn()
        } else {
            this.#circulationInfo.innerHTML = 'Остаток должен быть — 0,<br /> чтобы продолжить оформление заказа'
            this.#modal.disableSubmitBtn()
        }
    }

    #updateLayouts() {
        if (!this.#partData) return

        this.#emptyLayoutsContainer.innerHTML = ''
        this.#emptyLayoutsSerial = 0 + layoutsState[this.#partData.id].filter(state => !!state).length
        this.#filledLayoutsContainer.innerHTML = ''
        this.#filledLayoutsSerial = 0
        this.#qtyInputs = []
        this.#nameInputs = []

        layoutsState[this.#partData.id].forEach((layoutState, index) => {
            this.#layoutState = { layoutState, index }
        })
    }

    async #timeOut() {
        let timeout = 2000
        if (window.enfocus && window.pitStop.interval)
            timeout = window.pitStop.interval * 1000

        return new Promise((resolve, reject) => {
            setTimeout(() => resolve(true), timeout)
        })
    }

    async #uploadFile(file: File | undefined | null) {
        const calcModule = document.querySelector('form.calculation-module') as HTMLFormElement | null
        const dataProduct = calcModule?.dataset.product ?? ''

        if (file && window.partsData) {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('id', dataProduct) // При отрытии на странице с продуктом, нужно передавать id продукта
            formData.append('positionId', window.positionId ?? '') // Когда открываем в корзине
            formData.append('partId', this.#partData?.id ?? 'undefined')
            formData.append('partsData', JSON.stringify(window.partsData))
            formData.append('selectedParams', window.selectedParams ? JSON.stringify(window.selectedParams) : 'undefined')
            formData.append('species', this.#partData?.numberSpecies ?? 'undefined')

            this.#state = 'uploading'
            try {
                const uploadResData = await uploadWithProgress({
                    url: BASE_API_URL + window.uploadLayoutUrl,
                    data: formData,
                    container: this.#label,
                    span: this.#span
                }) as UploadRes


                let checkResData: CheckRes | undefined = undefined

                if (uploadResData.withCheck === 1) {
                    this.#state = 'check'
                    let attemts = 0

                    while (attemts < 100) {
                        const checkRes = await fetch(BASE_API_URL + uploadResData.checkUrl, {
                            signal: window.enfocus && window.pitStop.waiting_limit
                                ? AbortSignal.timeout(window.pitStop.waiting_limit * 1000)
                                : undefined
                        })
                        checkResData = await checkRes.json() as CheckRes

                        if (checkResData.success === -1)
                            throw new Error(checkResData.message)

                        if (checkResData.success === false)
                            throw new Error(checkResData.message)

                        if (checkResData.progress > 0)
                            this.#checkProgress = checkResData.progress

                        this.#span.textContent = `${this.#checkProgress} %`
                        this.#label.style.backgroundPosition = `${100 - this.#checkProgress}%`
                        this.#label.style.transitionDuration = '0.14s'

                        if (checkResData.success === 1) break

                        attemts++
                        await this.#timeOut()
                    }

                    this.#label.removeAttribute('style')
                    this.#checkProgress = 0
                }

                this.#state = 'ready'

                if (this.#partData) {
                    const emptyFieldIndex = layoutsState[this.#partData.id].findIndex(el => !el)

                    if (emptyFieldIndex < 0) return false
                    let temp = layoutsState[this.#partData.id].slice()
                    temp[emptyFieldIndex] = { partData: this.#partData, uploadResData, checkResData, ...getErrors(checkResData), index: emptyFieldIndex }
                    layoutsState[this.#partData.id] = temp

                    this.#updateLayouts()
                }

                return true

            } catch (e) {
                const err = e as Error
                this.#state = 'ready'
                this.#span.innerHTML = 'Выберите макет с компьютера'
                this.#label.removeAttribute('style')
                this.#checkProgress = 0

                createModal({
                    title: "Ошибка сервера",
                    subTitle: 'message' in err && err.message !== 'signal timed out'
                        ? err.message
                        : "Пожалуйста, попробуйте позже. Мы уже решаем проблему",
                    submitText: "Ok"
                }, this.#uploadZone)

            }
        }

        return false
    }

    set #layoutState({ layoutState, index }: { layoutState: LayoutState | null, index: number }) {
        if (!this.#partData) return

        if (layoutState) {
            const { errors, warnings } = getErrors(layoutState.checkResData)

            const resultContainer = document.createElement('div')
            resultContainer.classList.add('layout-type', 'result')
            resultContainer.classList.toggle('not-ok', errors || warnings)

            // Title ----------

            const span = document.createElement('span')
            const b = document.createElement('b')
            if (this.#partData)
                b.textContent = this.#partData.name.toUpperCase()
            span.append(b, ` ВИД ${++this.#filledLayoutsSerial}`)

            // File info ----------

            const resultElement = document.createElement('div')

            const resultFile = document.createElement('div')
            resultFile.classList.add('file')
            resultFile.classList.add(errors ? 'error' : warnings ? 'warning' : 'ok')
            const name = document.createElement('span')
            name.textContent = layoutState.uploadResData.original_name

            const closeBtn = document.createElement('button')
            closeBtn.append(createCloseIcon('var(--gray-500, #A0AEC0)'))
            closeBtn.onclick = () => createModal({
                title: 'Вы уверены что хотите отменить загрузку файлов?',
                subTitle: 'Все загруженные макеты будут утрачены',
                submitText: 'Да, отменить',
                cancelText: 'Не отменять',
                action: () => {
                    this.#state = 'ready'

                    let temp = layoutsState[layoutState.partData.id].slice()
                    temp[index] = null
                    layoutsState[layoutState.partData.id] = temp

                    this.#updateLayouts()
                }
            }, this.#uploadZone)

            const iconStatus = errors ? 'error' : warnings ? 'warning' : 'ok'
            resultFile.append(createInfoIcon(iconStatus), name, closeBtn)
            resultElement.append(resultFile)

            if (errors || warnings) {
                const errorLink = document.createElement('div')
                errorLink.classList.add('error-link')
                errorLink.textContent = errors ? 'Смотреть критичные замечания' : 'Смотреть замечания'
                errorLink.onclick = () => {
                    if (!this.#partData || !this.#modal.shadowRoot) return
                    createLayoutErrorsModal(layoutState, index, this.#modal.shadowRoot)
                }
                resultElement.append(errorLink)
            }

            // Inputs ----------

            const inputs = document.createElement('div')

            const nameInput = new MdOutlinedTextField
            nameInput.label = 'Наименование вида'
            nameInput.value = layoutState.name ?? ''
            nameInput.required = true
            nameInput.onchange = e => {
                let temp = layoutsState[layoutState.partData.id].slice()
                if (temp[index])
                    temp[index].name = (e.currentTarget as MdOutlinedTextField).value
                layoutsState[layoutState.partData.id] = temp
            }
            nameInput.oninput = e => {
                const field = e.currentTarget as MdOutlinedTextField
                if (field.validity.valid)
                    field.reportValidity()
            }
            this.#nameInputs.push(nameInput)

            const qtyInput = new MdOutlinedTextField
            qtyInput.type = 'number'
            qtyInput.min = '1'
            qtyInput.step = '1'
            qtyInput.label = 'Кол-во на тираж'
            qtyInput.value = `${layoutState.qty ?? ''}`
            nameInput.required = true
            qtyInput.onkeydown = e => {
                if (!e.key.match(/\d|Tab|Delete|Backspace/)) return false
            }
            qtyInput.oninput = e => {
                const field = e.currentTarget as MdOutlinedTextField
                const val = field.valueAsNumber

                if (isNaN(val)) {
                    delete this.#remainder[index]
                    this.#updateRemainder()
                    return
                }

                let temp = layoutsState[layoutState.partData.id].slice()
                if (temp[index])
                    temp[index].qty = val
                layoutsState[layoutState.partData.id] = temp
                this.#remainder = { ...this.#remainder, [index]: val }
            }
            qtyInput.onchange = e => {
                if (!this.#partData?.numberSpecies) return

                const typesQty = +this.#partData.numberSpecies
                const qtyList = Object.values(this.#remainder)

                if (!qtyList.length) return
                const sumWithoutLast = qtyList.reduce((acc, value, index) => {
                    if (index !== typesQty - 1)
                        return acc + value
                    else
                        return acc
                })
                const remainder = this.#circulationQty - sumWithoutLast

                if (qtyList.length >= typesQty - 1) {
                    const newValue = remainder > 0 ? remainder : 0
                    this.#remainder = { ...this.#remainder, [typesQty - 1]: newValue }
                    this.#qtyInputs[typesQty - 1].value = `${newValue}`
                }
            }
            this.#qtyInputs.push(qtyInput)

            inputs.append(nameInput, qtyInput)

            resultContainer.append(span, resultElement, inputs)

            this.#filledLayoutsContainer.append(resultContainer)

        } else {
            addEmptyLayout(this.#emptyLayoutsContainer, this.#partData.name, ++this.#emptyLayoutsSerial)
        }

        const hasState = layoutsState[this.#partData.id].find(state => !!state)
        this.#uploadZone.classList.toggle('empty', !hasState)
        if (hasState) {
            this.#circulationContainer.prepend(this.#circulationInfo)
            this.#circulationContainer.append(this.#circulationActions)
        } else if (this.#circulationContainer.children.length > 1) {
            this.#circulationContainer.removeChild(this.#circulationInfo)
            this.#circulationContainer.removeChild(this.#circulationActions)
        }

        setTimeout(() => {
            this.#modal.uploadView.classList.toggle('scrollable', this.#modal.uploadView.scrollHeight > this.#modal.uploadView.offsetHeight)

            if (this.#distributeInput.checked) {
                this.#remainder = {}
                this.#autofillQty()
                this.#qtyInputs.forEach(input => input.disabled = true)
            }

            if (this.#fillNamesInput.checked) {
                this.#autofillNames()
                this.#nameInputs.forEach(input => input.disabled = true)
            }
        })
    }

    get #state() {
        return this.#uploadState
    }

    set #state(state) {
        this.#uploadState = state
        this.#label.setAttribute('class', state)

        const span = document.createElement('span')
        span.classList.add('suffix')

        switch (state) {
            case 'ready':
                this.#span.textContent = 'Выберите макет с компьютера'
                this.#label.replaceChildren(...this.#readyElements, this.#span)
                this.#dropZoneContainer.replaceChildren(this.#label)
                break

            case 'uploading':
                this.#label.innerHTML = 'Идет загрузка макета'
                this.#label.prepend(createTimeIcon())
                span.textContent = 'KB'
                this.#label.append(this.#span, span)
                break

            case 'check':
                this.#label.innerHTML = 'Идет проверка макета'
                this.#label.prepend(createTimeIcon())
                this.#label.append(this.#span)
                break

            default:
                break
        }
    }

    get #remainder() {
        return this.#remainderValue
    }

    set #remainder(qtyListObj) {
        this.#remainderValue = qtyListObj
        this.#updateRemainder()
    }

    validate() {
        return !this.#nameInputs.map(input => {
            return input.reportValidity()
        }).includes(false)
    }

    get circulationDistributed() {
        if (!Object.values(this.#remainderValue).length) return false
        const sum = Object.values(this.#remainderValue).reduce((acc, value) => acc + value)
        const remainder = this.#circulationQty - sum
        return remainder === 0
    }

    get view() {
        return this.#uploadZone
    }
}